import { queryOptions } from "@tanstack/react-query";
import type { ProjectTemplateService } from "@/services/project-template.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * One template work item's attachments (NG-2799),
 * `GET {ws}/projects/templates/{id}/workitems/{wid}/attachments/`, as
 * `[slug, uid, 'project-template', templateId, 'workitem', workitemId, 'attachments']`.
 * Read by the template work-item panel's Attachments section.
 *
 * Under the template's own `'project-template'` key, so a prefix removal of a
 * deleted template reaches its attachments too.
 *
 * - **Mutable** (contract §2): each row carries a signed download URL, so it is
 *   reused for 30 s and re-read while the panel stays open.
 * - `"safe-read"`: a plain idempotent GET.
 *
 * @writers `WorkitemTemplateAttachmentsStore.uploadAttachment` prepends the
 * new attachment and `deleteAttachment` removes it at once, restoring it if the
 * delete fails, on the cached list.
 */
export function templateAttachmentsKey(scope: QueryScope, templateId: string, workitemId: string) {
  return scopedKey(scope, "project-template", templateId, "workitem", workitemId, "attachments");
}

export function templateAttachmentsQuery(
  service: Pick<ProjectTemplateService, "listWorkitemTemplateAttachments">,
  scope: QueryScope,
  templateId: string,
  workitemId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: templateAttachmentsKey(scope, templateId, workitemId),
    queryFn: scopedRead(
      (request) =>
        service.listWorkitemTemplateAttachments(scope.tenant, templateId, workitemId, request),
      { retry: "safe-read" },
    ),
  });
}
